/**
 * DiagnosticsOverlay — a hidden screen for checking layout on a real device.
 *
 * Opens only when a URL carries ?diag=1 (e.g. tap
 * https://backyard.lifestyle/profile?diag=1 in Notes on the phone; app links
 * open it in the app) and stays on for that session; ?diag=0 or × closes it.
 * Shows what the page sees: screen and viewport sizes, zoom, safe areas, the
 * viewport tag, and the widest elements — enough to tell from one screenshot
 * why a page doesn't fit the screen. Invisible to everyone else.
 */
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

const KEY = 'backyard:diag';

function wantDiag() {
  try {
    const q = new URLSearchParams(window.location.search).get('diag');
    if (q === '1') sessionStorage.setItem(KEY, '1');
    if (q === '0') sessionStorage.removeItem(KEY);
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function safeAreas() {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  document.body.appendChild(probe);
  const s = getComputedStyle(probe);
  const out = [s.paddingTop, s.paddingRight, s.paddingBottom, s.paddingLeft].map((v) => parseFloat(v) || 0).join(' / ');
  probe.remove();
  return out;
}

function measure() {
  const de = document.documentElement;
  const vv = window.visualViewport;
  const limit = de.clientWidth + 1;
  const wide = [...document.querySelectorAll('body *')]
    .map((e) => [e, e.getBoundingClientRect()])
    .filter(([e, b]) => b.width > limit && !e.closest('[data-diag]'))
    .sort((a, b) => b[1].width - a[1].width)
    .slice(0, 6)
    .map(([e, b]) => `${e.tagName.toLowerCase()}${e.id ? '#' + e.id : ''} ${String(e.className || '').slice(0, 70)} → w ${Math.round(b.width)}, left ${Math.round(b.left)}`);
  return [
    ['platform', `${Capacitor.getPlatform()} · ${navigator.userAgent.match(/OS [\d_]+/)?.[0] || ''}`],
    ['screen', `${window.screen.width} × ${window.screen.height} @${window.devicePixelRatio}x`],
    ['window.inner', `${window.innerWidth} × ${window.innerHeight}`],
    ['html client / scroll', `${de.clientWidth} / ${de.scrollWidth}`],
    ['body client / scroll', `${document.body.clientWidth} / ${document.body.scrollWidth}`],
    ['visualViewport', vv ? `${Math.round(vv.width)} × ${Math.round(vv.height)} · scale ${vv.scale.toFixed(3)} · left ${Math.round(vv.offsetLeft)}` : 'n/a'],
    ['font-size html / body', `${getComputedStyle(de).fontSize} / ${getComputedStyle(document.body).fontSize}`],
    ['safe areas t/r/b/l', safeAreas()],
    ['viewport tag', document.querySelector('meta[name="viewport"]')?.content || 'none'],
    ['path', window.location.pathname],
    ['wider than html', wide.length ? wide.join('\n') : 'none'],
  ];
}

export default function DiagnosticsOverlay() {
  const [on, setOn] = useState(wantDiag);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const check = () => setOn(wantDiag());
    window.addEventListener('popstate', check);
    const t = setInterval(check, 1500);
    return () => { window.removeEventListener('popstate', check); clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!on) return undefined;
    const tick = () => setRows(measure());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [on]);

  if (!on) return null;
  return (
    <div data-diag style={{
      position: 'fixed', left: 8, top: 'calc(env(safe-area-inset-top) + 8px)', width: 'calc(100vw - 16px)',
      maxWidth: 360, zIndex: 99999, background: 'rgba(17,24,39,0.92)', color: '#fff', borderRadius: 12,
      padding: 10, font: '11px/1.35 ui-monospace, Menlo, monospace', boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginBottom: 6 }}>
        <span>Layout check</span>
        <button type="button" onClick={() => { try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } setOn(false); }}
          style={{ color: '#fff', background: 'transparent', border: 0, fontSize: 16, lineHeight: 1 }} aria-label="Close">×</button>
      </div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ marginBottom: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          <span style={{ color: '#F4D341' }}>{k}:</span> {v}
        </div>
      ))}
    </div>
  );
}
